import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TdgService } from "../../../core/services/tdg.service";
import { UtilitiesService } from "../../../core/services/utilities.service";
import { ValidationWarningModalComponent } from "../../modal-templates/validation-warning-modal/validation-warning-modal.component";
import { MaterialModule } from "../../../core/shared/material.module";
import { ZorroTableComponent } from "../custom-table/zorro-table.component";

@Component({
  selector: "app-multi-tab-table",
  standalone: true,
  imports: [ZorroTableComponent, CommonModule, MaterialModule],
  templateUrl: "./multi-tab-table.component.html",
  styleUrl: "./multi-tab-table.component.scss",
})
export class MultiTabTableComponent implements OnInit {
  @Input() result: any = {};
  @Output() tableRowSelect = new EventEmitter<any>();
  @Output() selectedTab = new EventEmitter<any>();
  @Output() onAction = new EventEmitter<any>();

  @ViewChild(ZorroTableComponent) tableComponent!: ZorroTableComponent;

  schemas: any;
  isLoading: boolean = false;
  selectedSchema: any = [];
  isRegen: boolean = false;
  isSave: boolean = false;
  isNewRow: boolean = false;
  public isSaving: boolean = false;
  selectedRows: any = [];
  pageSize = 5;
  currentPage: any = 1;
  totalRecords: any;
  startRow: any;
  endRow: any;
  errorData: any;
  errorText: string = "";
  isFileValidation: boolean = false;
  modalService = inject(NgbModal);

  constructor(
    private readonly tdgService: TdgService,
    private readonly utils: UtilitiesService,
  ) {}
  ngOnInit() {
    const formattedSchemas = this.result?.combined_tables.map((table: any) => {
      const formattedRawData = table.rawData.map((row: any[]) =>
        row.map((cell: any) => `'${cell}'`).join(", "),
      );
      const piiColumns = table.piiColumns ?? table.pii_columns ?? [];

      return {
        db_name: table.testCaseId,
        testCaseId: table.testCaseId,
        columns: table.columns,
        piiColumns,
        pii_columns: piiColumns,
        messages: [],
        connectionId: this.tdgService
          .getSelectedSchemas()
          .find(
            (ele: any) =>
              ele.connectionName.replaceAll(".sql", "") ===
              this.result?.db_name,
          )?.id,
        tables: [
          {
            tableName: table.testCaseId,
            columns: table.columns,
            rawData: table?.columns.length > 0 ? formattedRawData : [],
            piiColumns,
            pii_columns: piiColumns,
          },
        ],
      };
    });
    this.getValidationErrors(this.result?.validation_errors);
    this.schemas = this.tdgService.getFormatedResponse(formattedSchemas, true);
    // this.selectedSchema = this.schemas.length > 0 ? this.schemas[0] : [];
    this.restoreSelectedTab();
    this.selectedTab.emit(this.selectedSchema);

    this.currentPage = 1;
    this.totalRecords = this.selectedSchema?.tables[0]?.rawData?.length;
    this.getRowCounts();
  }

  getValidationErrors(errors: any) {
    if (errors.length > 0) {
      let data = errors;
      this.isFileValidation = false;
      this.errorData = data;

      if (data[0]?.type === "FILE_VALIDATION") {
        this.isFileValidation = true;
        this.errorData = data.filter(
          (item: any) => item.type === "FILE_VALIDATION",
        );
      } else {
        this.isFileValidation = false;
        this.errorData = data.filter(
          (item: any) => item.type === "COMPATIBILITY",
        );
      }

      this.errorText = this.isFileValidation
        ? this.errorData?.length + " file(s) failed file validation."
        : this.errorData?.length +
          " test case(s) failed compatibility validation.";
      this.openValidationError();
    }
  }

  openValidationError() {
    let modalRef = this.modalService.open(ValidationWarningModalComponent, {
      centered: true,
      backdrop: "static",
      size: "lg",
    });

    modalRef.componentInstance.configs = {
      title: this.isFileValidation
        ? "File Validation Failed"
        : "Compatibility Issues Detected",
      subTitle: this.isFileValidation
        ? this.errorData.length + " file(s) failed validation"
        : this.errorData.length +
          " test case(s) failed compatibility validation",
      requiredAction: this.isFileValidation
        ? " Fix the following validation errors and re-upload your files to continue."
        : "Review and resolve the following compatibility issues before continuing with test data generation. ",
      tip: this.isFileValidation
        ? "Click on the file name to expand and view detailed instructions."
        : "Expand each test case to view detailed recommendations",
      data: this.errorData,
      buttonText: "OK",
    };
  }

  ngAfterViewInit() {
    this.tableComponent.pageIndex = 1;
  }

  switchTab(schema: any) {
    this.tableComponent.pageIndex = 1;
    this.selectedSchema = schema;
    localStorage.setItem(this.SELECTED_TAB_KEY, JSON.stringify(schema));
    this.totalRecords = this.selectedSchema?.tables[0]?.rawData?.length;
    this.getRowCounts();
    this.selectedTab.emit(this.selectedSchema);
  }

  private readonly SELECTED_TAB_KEY = "tdg_selected_tab";

  restoreSelectedTab() {
    const saved = localStorage.getItem(this.SELECTED_TAB_KEY);
    if (!saved) {
      this.selectedSchema = this.schemas.length > 0 ? this.schemas[0] : [];
    } else {
      const parsed = JSON.parse(saved);
      const match = this.schemas.find(
        (s: any) =>
          s.testCaseId === parsed.testCaseId || s.db_name === parsed.db_name,
      );

      if (match) {
        this.selectedSchema = match;
      } else {
        this.selectedSchema = this.schemas.length > 0 ? this.schemas[0] : [];
      }
    }
  }

  onSelectAll($event: any) {}

  onClickAction(type: string) {
    this.onAction.emit(type);
  }

  onTableRowSelect(event: any) {
    this.selectedRows = event?.data;
    this.tableRowSelect.emit(event);
  }

  getRowCounts() {
    this.startRow = (this.currentPage - 1) * this.pageSize + 1;
    this.endRow = Math.min(this.currentPage * this.pageSize, this.totalRecords);
  }

  onPageChange(event: any) {
    this.currentPage = event?.page;
    this.pageSize = event?.size ? event?.size : this.pageSize;
    this.getRowCounts();
  }
}
